import { useState, useRef, useEffect } from 'react';
import api from '../../services/api';
import {
  parseAddressPredictions,
  parseAddressDetails,
  shouldOpenPredictionList
} from '../../utils/addressPredictions';
import styles from './AddressAutocomplete.module.css';

/**
 * AddressAutocomplete — Google Places-powered address field
 * Props:
 *   value: string (current address input)
 *   onChange: (value: string) => void
 *   onSelect: ({ street, city, state, zipCode }) => void
 *   placeholder: string
 *   label: string
 */
export default function AddressAutocomplete({ value, onChange, onSelect, placeholder = 'Start typing your address...', label = 'Street Address', name }) {
  const [predictions, setPredictions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  // A lookup OUTAGE is not "no matching address". Without this the two are the same
  // pixels: an empty dropdown. Card 53566.
  const [lookupError, setLookupError] = useState('');
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchPredictions = async (input) => {
    if (!input || input.length < 3) {
      setPredictions([]);
      setShowDropdown(false);
      setLookupError('');
      return;
    }
    setLoading(true);
    setLookupError('');
    try {
      const res = await api.get(`/users/address/autocomplete?input=${encodeURIComponent(input)}`);
      const preds = parseAddressPredictions(res);
      setPredictions(preds);
      setShowDropdown(shouldOpenPredictionList(preds));
    } catch (err) {
      // SURFACE, never throw. The street `<input>` is controlled by the parent and is
      // fully usable without this dropdown, so a Places outage must not block a member
      // from typing their address by hand — it must only stop pretending there were no
      // matches. `showDropdown` stays true so the message is actually reachable.
      setPredictions([]);
      setShowDropdown(true);
      setLookupError(err?.message || 'Address lookup is unavailable right now.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    onChange(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPredictions(val), 300);
  };

  const handleSelect = async (prediction) => {
    setShowDropdown(false);
    setPredictions([]);
    setLookupError('');
    onChange(prediction.description);
    try {
      const res = await api.get(`/users/address/place-details?placeId=${encodeURIComponent(prediction.placeId)}`);
      const details = parseAddressDetails(res);
      if (details) {
        onSelect(details);
      }
    } catch {
      // Still set the description even if place details fail
    }
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      {label && <label className={styles.label}>{label}</label>}
      <div className={styles.inputWrapper}>
        <input
          type="text"
          name={name}
          value={value}
          onChange={handleInputChange}
          onFocus={() => predictions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          className={styles.input}
          autoComplete="off"
        />
        {loading && <span className={styles.spinner} />}
      </div>
      {showDropdown && (lookupError || predictions.length > 0) && (
        <ul className={styles.dropdown}>
          {lookupError ? (
            <li className={styles.errorItem} role="alert">
              <svg className={styles.errorIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="13" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>
                {lookupError}
                <span className={styles.errorHint}>
                  This is not the same as no matching address — you can still type your address in full.
                </span>
              </span>
            </li>
          ) : (
            predictions.map((p, i) => (
              <li key={p.placeId || i} className={styles.item} onClick={() => handleSelect(p)}>
                <svg className={styles.pinIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                <span>{p.description}</span>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
